import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Alert, Gallery } from "@icg360/design-system";

import { BaseLayout } from "components/base-layout";
import { Stack } from "components/common/stack";
import { InsuranceRepresentative } from "components/shared/insurance-representative";
import { MainLayout } from "components/shared/main-layout";
import { type KeystoneReferral } from "data/keystone-api-mapping";
import { usePolicyDetail } from "data/ss-data-hooks";
import { checkForNaStringValue, downloadImage } from "utils";

import { useInspectionsData } from "../hooks";
import galleryMediaTileStyles from "../inspection-gallery-media-tiles.module.scss";
import { type MediaTileItem } from "../inspection-gallery-types";
import {
  buildInspectionAssetDescription,
  sortInspectionAssetIdsByLastUpdatedDesc,
} from "../inspections-helpers";
import { IneligibleWriteOutCard } from "./ineligible-write-out-card";
import styles from "./inspections-ineligible.module.scss";

type IneligibleGalleryData = {
  assetIds: string[];
  title: string;
  subHeading: string;
  activeAssetId?: string;
};

export const InspectionsIneligible = () => {
  const navigate = useNavigate();
  const { data } = usePolicyDetail();
  const { agencyName } = data?.userInsuranceRep ?? {};
  const [galleryData, setGalleryData] = useState<IneligibleGalleryData | null>(
    null,
  );
  const { latestInspection, isLoading: loading } = useInspectionsData();

  const inspectionAssets = latestInspection?.review?.inspectionReviewAssets;
  const statusEnum = latestInspection?.status;

  useEffect(() => {
    if (statusEnum && statusEnum !== 480) {
      navigate("/my/overview");
    }
  }, [navigate, statusEnum]);

  const sortedGalleryAssetIds = useMemo(() => {
    const ids = galleryData?.assetIds;
    if (!ids?.length) {
      return [];
    }
    return sortInspectionAssetIdsByLastUpdatedDesc(ids, inspectionAssets);
  }, [galleryData?.assetIds, inspectionAssets]);

  const galleryActiveIndex = useMemo(() => {
    const id = galleryData?.activeAssetId;
    if (!id || sortedGalleryAssetIds.length === 0) {
      return 0;
    }
    const idx = sortedGalleryAssetIds.indexOf(id);
    return idx >= 0 ? idx : 0;
  }, [galleryData?.activeAssetId, sortedGalleryAssetIds]);

  if (loading) {
    return <BaseLayout loading />;
  }

  const referralList = latestInspection?.review?.referrals ?? [];
  const sortedReferralList = referralList.toSorted((referral) => {
    return referral?.category === "Roof" ? -1 : 1;
  });

  const ineligibleReferrals: KeystoneReferral[] = [];
  const otherReferrals: KeystoneReferral[] = [];
  (sortedReferralList ?? []).forEach((referral) => {
    if (referral?.actionStatement === "Risk is ineligible") {
      ineligibleReferrals.push(referral);
    } else if (referral) {
      otherReferrals.push(referral);
    }
  });

  const referralWriteOutEntries = [
    ...ineligibleReferrals.map((referral, idx) => ({
      referral,
      key: `ineligible-${referral.id ?? idx}`,
    })),
    ...otherReferrals.map((referral, idx) => ({
      referral,
      key: `other-${referral.id ?? idx}`,
    })),
  ];

  const openGalleryForReferral = (referral: KeystoneReferral) => {
    return (activeAssetId?: string) => {
      if (!referral.inspectionReviewAssetIds?.length) {
        return;
      }
      setGalleryData({
        assetIds: referral.inspectionReviewAssetIds ?? [],
        title: checkForNaStringValue(referral.issueStatement ?? "") ?? "",
        subHeading: checkForNaStringValue(referral.locationDetail ?? "") ?? "",
        activeAssetId,
      });
    };
  };

  if (galleryData != null && sortedGalleryAssetIds.length > 0) {
    const media: MediaTileItem[] = sortedGalleryAssetIds.map((assetId) => {
      const selectedAsset = inspectionAssets?.find(
        (asset) => asset.id === assetId,
      );
      const fileName = selectedAsset?.assetFileName;

      return {
        fileName: fileName ?? `image_${assetId}`,
        description: buildInspectionAssetDescription(
          selectedAsset,
          agencyName,
          "ineligible",
        ),
        id: assetId,
        source: `/api/inspection/image/${assetId}`,
      };
    });

    return (
      <Gallery
        className={galleryMediaTileStyles.galleryRoot}
        media={media}
        activeIndex={galleryActiveIndex}
        title={galleryData.title}
        subheading={galleryData.subHeading}
        onClose={() => setGalleryData(null)}
        activeAction="Download"
        showHeroFooter={true}
        onActiveAction={(active: MediaTileItem) => {
          downloadImage(active?.source ?? "", active.fileName);
        }}
      />
    );
  }

  return (
    <BaseLayout>
      <MainLayout
        topRow={
          <Stack gap="md" className={styles.topRow}>
            <Alert
              appearance="danger"
              description="We may have other carriers that work for your property. Please contact your insurance rep to explore your options."
              title="Your property is ineligible for this policy."
            />
          </Stack>
        }
        sidebar={<InsuranceRepresentative />}
      >
        <Stack gap="xl">
          {referralWriteOutEntries.map(({ referral, key }) => (
            <IneligibleWriteOutCard
              key={key}
              referral={referral}
              showGallery={openGalleryForReferral(referral)}
            />
          ))}
        </Stack>
      </MainLayout>
    </BaseLayout>
  );
};
